import { CombinedFlowAdapter, KeywordProperties, AliasedExpression } from './combined.flow.adapter';
import { QueryExpressionParserService, ChipExpression } from '@nrwl/query-emitter';
import { cloneDeep } from 'lodash';

// this processor should convert an array of expressions into one SQL string,
// understood by analytics plugin.
export class FilterFlowSecAdapter extends CombinedFlowAdapter {
  // eslint-disable-next-line
  constructor(private readonly queryBuilder: QueryExpressionParserService, config: any) {
    super(config);
  }

  // eslint-disable-next-line
  processValue(value: any): any {
    // to stay compatible with original implementation, we must return '' if value is not defined
    if (!value) {
      return '';
    }
    // if value is anything else than chip expressions, keep the value intact
    if (!this.isChipExpressionBased(value)) {
      return value; // don't change it.
    }
    // and only then attempt converting value to desired format
    const result = super.processValue(value);
    return result.expression;
  }

  /*
    Current implementation is trivial: build a simple logical string with AND/OR, etc.
    In some future iterations, we might have to solve more complex cases. For example,
    Location search might be mapped to lat/long rectangle (eg, 'lat1 > N and lat1 < N+1 and lon1 > M and lon1 < M+1').
    This may require to analyze location input (eg, 'Boston MA, US') and consult with geo service to figure out lat/lon.
    If this need becomes real, then I'd suggest to change the output on 'smart search' so that it takes care to resolve such
    things upfront.
  */
  build(value: ChipExpression[], mappingTable: Map<string, KeywordProperties>): AliasedExpression {
    const aliases = {
      source: {},
      destination: {}
    };
    const copy = cloneDeep(value);
    // step one. replace UI-keywords with db field names
    copy.forEach(expression => {
      this.chooseNamesAndAliases(expression, mappingTable, aliases);
    });
    // step two - build sql like string. We could use PEG grammar to do that for us (eg, bind 'stringify' action to grammar nodes),
    // but that requires from us to restore original string (out of ChipExpressions...).
    return {
      expression: this.queryBuilder.toExpression(copy),
      aliases: aliases
    };
  }

  private chooseNamesAndAliases(expression, mappingTable, aliases) {
    const mappedName = mappingTable.get(expression.keyword);
    if (!mappedName) {
      // no mapping found. send raw field name
      return;
    }
    if (!this.useAliases()) {
      // right now we still use some KPIs that are not based on directional logic
      // (even if it looks like this is wrong, and they will have to be made directional)
      // hence we want to use 'raw' field names, such as site2, user1, etc.
      expression.keyword = mappedName.fieldName;
      return;
    }
    switch (mappedName.placement) {
      case 'no-alias':
        expression.keyword = mappedName.fieldName;
        break;
      case 'no-direction':
        {
          expression.keyword = mappedName.alias;
          // in case of 'no-direction', aliases do not need to be populated.
        }
        break;
      case 'src':
        {
          expression.keyword = mappedName.alias;
          aliases.source[mappedName.alias] = mappedName.fieldPrefix;
        }
        break;
      case 'dst':
        {
          expression.keyword = mappedName.alias;
          aliases.source[mappedName.alias] = mappedName.fieldPrefix;
        }
        break;
    }
  }
}
